@if (isLoading() && !effectivePermissions()) {
  <p
    class="loading-text"
    data-testid="effective-permissions-loading"
    role="status"
    aria-live="polite"
    [appTerminologyIdentity]="usage.loading"
  >{{ text.loading() }}</p>
} @else if (error(); as error) {
  <div class="message-box error" data-testid="effective-permissions-error" role="alert" aria-live="assertive">
    <p>{{ error.title }}</p>
    <ul>
      @for (message of error.messages; track message) {
        <li>{{ message }}</li>
      }
    </ul>
    @if (error.problemCode || error.correlationId) {
      <div class="error-meta">
        @if (error.problemCode) {
          <span><span [appTerminologyIdentity]="usage.problemCodeLabel">{{ text.problemCodeLabel() }}</span>: {{ error.problemCode }}</span>
        }
        @if (error.correlationId) {
          <span><span [appTerminologyIdentity]="usage.correlationIdLabel">{{ text.correlationIdLabel() }}</span>: {{ error.correlationId }}</span>
        }
      </div>
    }
    <button
      type="button"
      class="effective-permissions__button"
      data-testid="effective-permissions-retry"
      [appTerminologyIdentity]="usage.retryAction"
      (click)="load()"
    >{{ text.retryAction() }}</button>
  </div>
} @else {
  @if (isInactive()) {
    <p
      class="inline-note effective-permissions__notice"
      data-testid="effective-permissions-inactive"
      role="status"
      [appTerminologyIdentity]="usage.inactiveNotice"
    >{{ text.inactiveNotice() }}</p>
  }

  <div class="effective-permissions__legend" data-testid="effective-permissions-legend">
    <span class="effective-permissions__legend-label" [appTerminologyIdentity]="usage.legendSource">{{ text.legendSource() }}</span>
    <span class="effective-permissions__legend-item">
      <span class="effective-permissions__swatch effective-permissions__swatch--role" aria-hidden="true"></span>
      <span [appTerminologyIdentity]="usage.legendRole">{{ text.legendRole() }}</span>
    </span>
    <span class="effective-permissions__legend-item">
      <span class="effective-permissions__swatch effective-permissions__swatch--direct" aria-hidden="true"></span>
      <span [appTerminologyIdentity]="usage.legendDirect">{{ text.legendDirect() }}</span>
    </span>
    <span class="effective-permissions__legend-item">
      <span class="effective-permissions__swatch effective-permissions__swatch--none" aria-hidden="true"></span>
      <span [appTerminologyIdentity]="usage.notGranted">{{ text.notGranted() }}</span>
    </span>
    <span class="effective-permissions__legend-note" [appTerminologyIdentity]="usage.legendNote">{{ text.legendNote() }}</span>
  </div>

  <div class="effective-permissions__toolbar">
    <label class="effective-permissions__filter" for="effective-permissions-filter-input">
      <span [appTerminologyIdentity]="usage.filterLabel">{{ text.filterLabel() }}</span>
      <input
        id="effective-permissions-filter-input"
        type="search"
        autocomplete="off"
        data-testid="effective-permissions-filter"
        [value]="filter()"
        (input)="onFilter($event)"
      />
    </label>
    <p class="effective-permissions__counts" data-testid="effective-permissions-counts" aria-live="polite">
      <strong>{{ counts().granted }} <span [appTerminologyIdentity]="usage.countsGranted">{{ text.countsGranted() }}</span>:</strong>{{ ' ' }}<span class="effective-permissions__count--role">{{ counts().fromRoles }} <span [appTerminologyIdentity]="usage.countsFromRoles">{{ text.countsFromRoles() }}</span></span>{{ ' · ' }}<span class="effective-permissions__count--direct">{{ counts().adHoc }} <span [appTerminologyIdentity]="usage.countsAdHoc">{{ text.countsAdHoc() }}</span></span>
      @if (counts().both > 0) {
        {{ ' ' }}<span class="effective-permissions__count--both">({{ counts().both }} <span [appTerminologyIdentity]="usage.countsFromBoth">{{ text.countsFromBoth() }}</span>)</span>
      }
    </p>
  </div>

  @if (emptyState() === 'no-matches') {
    <div class="empty-state effective-permissions__empty" data-testid="effective-permissions-empty" role="status">
      <span><span [appTerminologyIdentity]="usage.noMatches">{{ text.noMatches() }}</span> "{{ filter().trim() }}"</span>
      <button
        type="button"
        class="effective-permissions__button"
        data-testid="effective-permissions-clear-filter"
        [appTerminologyIdentity]="usage.clearFilterAction"
        (click)="clearFilter()"
      >{{ text.clearFilterAction() }}</button>
    </div>
  } @else if (emptyState() === 'none-granted') {
    <p
      class="empty-state effective-permissions__empty"
      data-testid="effective-permissions-empty"
      role="status"
      [appTerminologyIdentity]="usage.noneGranted"
    >{{ text.noneGranted() }}</p>
  } @else {
    <ul
      class="effective-permissions__grid"
      role="list"
      [attr.aria-label]="text.listLabel() + ' ' + (displayName() || text.selectedUserFallback())"
      [attr.aria-busy]="isLoading()"
    >
      @for (tile of filteredTiles(); track tile.code) {
        <li
          class="effective-permission"
          [class.effective-permission--role]="tile.origin === 'role'"
          [class.effective-permission--direct]="tile.origin === 'direct'"
          [class.effective-permission--both]="tile.origin === 'both'"
          [class.effective-permission--none]="tile.origin === 'none'"
          [class.effective-permission--locked]="isRoleLocked(tile)"
          data-testid="effective-permission-row"
          [attr.data-code]="tile.code"
          [attr.data-origin]="tile.origin"
          [title]="tile.code"
        >
          <input
            type="checkbox"
            class="effective-permission__toggle"
            data-testid="effective-permission-toggle"
            [id]="toggleId(tile)"
            [checked]="tile.granted"
            [disabled]="isToggleDisabled(tile)"
            [attr.title]="toggleTitle(tile)"
            (change)="onToggle(tile, $event)"
          />
          <label class="effective-permission__name" [for]="toggleId(tile)">{{ tile.name }}</label>
          @if (tile.granted) {
            <span class="effective-permission__pills">
              @for (source of tile.roleSources; track $index) {
                <span
                  class="effective-permission__pill effective-permission__pill--role"
                  [attr.title]="isRoleLocked(tile) ? text.roleOnlyTitle() : null"
                >{{ source.roleName || source.roleCode }} <span [appTerminologyIdentity]="usage.pillRoleSuffix">{{ text.pillRoleSuffix() }}</span></span>
              }
              @if (tile.fromDirect) {
                <span class="effective-permission__pill effective-permission__pill--direct" [appTerminologyIdentity]="usage.pillAdHoc">{{ text.pillAdHoc() }}</span>
              }
            </span>
          }
          @for (scope of tile.scopes; track scope) {
            <span class="effective-permission__scope">{{ scope }}</span>
          }
        </li>
      }
    </ul>
  }
}
